import { AlertTriangle } from "lucide-react";

import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import { useRouteMutations } from "../../hooks/useRouteMutations";
import type { CorridorRouteCell } from "../../types/paymentRoutes.types";
import { corridorCellLabel } from "../../utils/routeFormat";

export interface DeleteRouteDialogProps {
  /** Cell whose route is being removed — always one with a `route`. */
  cell: CorridorRouteCell;
  open: boolean;
  onClose: () => void;
  /** `wasMissing` is true when the row was already gone (404) — the caller still refreshes, without an error. */
  onDeleted: (wasMissing: boolean, corridorLabel: string) => void;
}

/**
 * Deletion confirmation.
 *
 * The escalation is the whole point of this dialog: unlike a fee version, a route has no fallback. Removing
 * it leaves the corridor with nothing to resolve, so every payin and payout there is refused with `no_route`
 * from that instant until another route exists — which is why the consequence names the corridor instead of
 * saying "this cannot be undone".
 *
 * It also states the legitimate use: freeing the corridor is the required first step to point it at another
 * provider, because the unique index has no room for a second row.
 */
export function DeleteRouteDialog({ cell, open, onClose, onDeleted }: DeleteRouteDialogProps) {
  const { t } = useTranslations("paymentRoutes");
  const labels = useFeeLabels();
  const { isSubmitting, error, removeRoute } = useRouteMutations();

  const corridorLabel = corridorCellLabel(cell, labels.directionShort);
  const isBlocked = cell.status === "BLOCKED";

  const handleConfirm = async () => {
    if (!cell.route) return;
    const outcome = await removeRoute(cell.route.id);
    if (outcome !== "error") onDeleted(outcome === "missing", corridorLabel);
  };

  return (
    <ColocaAlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose();
      }}
      title={t("delete_title")}
      description={t("delete_semantics")}
      confirmLabel={t("delete_confirm")}
      confirmVariant="destructive"
      confirmDisabled={isSubmitting}
      cancelLabel={t("delete_cancel")}
      onConfirm={() => void handleConfirm()}
      onCancel={onClose}
    >
      <div className="space-y-3">
        {/* What exactly is being removed — corridor, direction and the model it was running under. */}
        <div className="space-y-2 rounded-xl border border-border bg-card p-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <ColocaBadge variant="default">{cell.country}</ColocaBadge>
            <ColocaBadge variant="secondary" className="font-sans">
              {formatRail(cell.rail)}
            </ColocaBadge>
            <span className="font-mono text-xs text-muted-foreground">{cell.currency}</span>
            <ColocaBadge variant="default" className="ml-auto font-sans">
              {labels.direction[cell.direction]}
            </ColocaBadge>
          </div>
          {cell.route && (
            <p className="text-xs text-muted-foreground">{labels.model[cell.route.economic_model]}</p>
          )}
        </div>

        {/* An inactive route is already refusing everything, so promising that deletion is what breaks the
            corridor would be false — and would discourage the only repair available for that state. */}
        <ColocaAlert
          variant={isBlocked ? "warning" : "destructive"}
          icon={AlertTriangle}
          text={t(isBlocked ? "delete_consequence_blocked" : "delete_consequence", {
            corridor: corridorLabel,
          })}
        />
        <p className="text-xs text-muted-foreground">{t("delete_frees_corridor")}</p>

        {error && <ColocaAlert variant="destructive" text={error} />}
      </div>
    </ColocaAlertDialog>
  );
}
